export const READINESS_CHECK_COPY = {
  identity: {
    label: 'CAPTAIN_EXPERIENCE.WORKSPACE.READINESS_CHECK_IDENTITY',
    helper: 'CAPTAIN_EXPERIENCE.WORKSPACE.READINESS_CHECK_IDENTITY_HELPER',
  },
  instructions: {
    label: 'CAPTAIN_EXPERIENCE.WORKSPACE.READINESS_CHECK_INSTRUCTIONS',
    helper: 'CAPTAIN_EXPERIENCE.WORKSPACE.READINESS_CHECK_INSTRUCTIONS_HELPER',
  },
  knowledge: {
    label: 'CAPTAIN_EXPERIENCE.WORKSPACE.READINESS_CHECK_KNOWLEDGE',
    helper: 'CAPTAIN_EXPERIENCE.WORKSPACE.READINESS_CHECK_KNOWLEDGE_HELPER',
  },
  handoff: {
    label: 'CAPTAIN_EXPERIENCE.WORKSPACE.READINESS_CHECK_HANDOFF',
    helper: 'CAPTAIN_EXPERIENCE.WORKSPACE.READINESS_CHECK_HANDOFF_HELPER',
  },
  channel: {
    label: 'CAPTAIN_EXPERIENCE.WORKSPACE.READINESS_CHECK_CHANNEL',
    helper: 'CAPTAIN_EXPERIENCE.WORKSPACE.READINESS_CHECK_CHANNEL_HELPER',
  },
  tested: {
    label: 'CAPTAIN_EXPERIENCE.WORKSPACE.READINESS_CHECK_TESTED',
    helper: 'CAPTAIN_EXPERIENCE.WORKSPACE.READINESS_CHECK_TESTED_HELPER',
  },
  rollout: {
    label: 'CAPTAIN_EXPERIENCE.WORKSPACE.READINESS_CHECK_ROLLOUT',
    helper: 'CAPTAIN_EXPERIENCE.WORKSPACE.READINESS_CHECK_ROLLOUT_HELPER',
  },
  scenario_tools: {
    label: 'CAPTAIN_EXPERIENCE.WORKSPACE.READINESS_CHECK_SCENARIO_TOOLS',
    helper:
      'CAPTAIN_EXPERIENCE.WORKSPACE.READINESS_CHECK_SCENARIO_TOOLS_HELPER',
  },
  approval_runtime: {
    label: 'CAPTAIN_EXPERIENCE.WORKSPACE.READINESS_CHECK_APPROVAL_RUNTIME',
    helper:
      'CAPTAIN_EXPERIENCE.WORKSPACE.READINESS_CHECK_APPROVAL_RUNTIME_HELPER',
  },
};

const READINESS_STATUS_COPY = {
  complete: 'CAPTAIN_EXPERIENCE.WORKSPACE.READINESS_STATUS_COMPLETE',
  blocker: 'CAPTAIN_EXPERIENCE.WORKSPACE.READINESS_STATUS_BLOCKER',
  warning: 'CAPTAIN_EXPERIENCE.WORKSPACE.READINESS_STATUS_WARNING',
  pending: 'CAPTAIN_EXPERIENCE.WORKSPACE.READINESS_STATUS_PENDING',
  stale: 'CAPTAIN_EXPERIENCE.WORKSPACE.READINESS_STATUS_STALE',
};

const TEST_STATUS_HELPER_COPY = {
  current: 'CAPTAIN_EXPERIENCE.WORKSPACE.READINESS_CHECK_TESTED_CURRENT_HELPER',
  stale: 'CAPTAIN_EXPERIENCE.WORKSPACE.READINESS_CHECK_TESTED_STALE_HELPER',
  missing: 'CAPTAIN_EXPERIENCE.WORKSPACE.READINESS_CHECK_TESTED_MISSING_HELPER',
};

const isRecord = value =>
  Boolean(value) && typeof value === 'object' && !Array.isArray(value);

const nonNegativeNumber = value => {
  const number = Number(value);
  return Number.isFinite(number) ? Math.max(0, number) : 0;
};

export const readAssistantReadiness = management => {
  const readiness = isRecord(management?.readiness) ? management.readiness : {};

  return {
    checks: isRecord(readiness.checks) ? readiness.checks : {},
    completed: nonNegativeNumber(readiness.completed),
    total: nonNegativeNumber(readiness.total),
    ready: readiness.ready === true,
    testStatus: ['current', 'stale', 'missing'].includes(readiness.test_status)
      ? readiness.test_status
      : 'missing',
    blockers: Array.isArray(readiness.blockers) ? readiness.blockers : [],
    warnings: Array.isArray(readiness.warnings) ? readiness.warnings : [],
  };
};

export const mapReadinessChecks = (
  checks,
  translate,
  { blockers = [], warnings = [], testStatus = 'missing' } = {}
) => {
  const blockerByKey = new Map(
    blockers.map(issue => [String(issue?.key || ''), issue])
  );
  const warningByKey = new Map(
    warnings.map(issue => [String(issue?.key || ''), issue])
  );
  const checkEntries = new Map(Object.entries(checks));
  [...blockers, ...warnings].forEach(issue => {
    const key = String(issue?.key || '');
    if (READINESS_CHECK_COPY[key] && !checkEntries.has(key)) {
      checkEntries.set(key, false);
    }
  });

  return Array.from(checkEntries.entries())
    .filter(([key]) => READINESS_CHECK_COPY[key])
    .map(([key, done]) => {
      const blocker = blockerByKey.get(key);
      const warning = warningByKey.get(key);
      let severity = 'pending';
      if (done) severity = 'complete';
      else if (blocker) severity = 'blocker';
      else if (warning) severity = 'warning';

      const resolvedTestStatus = key === 'tested' ? testStatus : null;
      const testHelperKey = TEST_STATUS_HELPER_COPY[resolvedTestStatus];
      const statusCopy =
        resolvedTestStatus === 'stale'
          ? READINESS_STATUS_COPY.stale
          : READINESS_STATUS_COPY[severity];

      return {
        key,
        label: translate(READINESS_CHECK_COPY[key].label),
        helper:
          (testHelperKey && translate(testHelperKey)) ||
          blocker?.message ||
          warning?.message ||
          translate(READINESS_CHECK_COPY[key].helper),
        done: done === true,
        severity,
        statusLabel: translate(statusCopy),
      };
    });
};

export const readinessPercentage = ({ completed, total }) =>
  total > 0 ? Math.round(Math.min(100, (completed / total) * 100)) : 0;
